import {
  ApiError,
  approveContent,
  contentPath,
  retryContent,
  retryContentSetup,
  retryIdeaBatch,
  startHeadlessPlanning,
  startProduction,
} from '../api.ts';
import type { IconName } from '../Icon.tsx';
import type { Toaster } from '../toast.tsx';
import * as wording from '../wording.ts';
import type { BoardCard, ContentCard, IdeaCard } from './model.ts';

/**
 * The one action that moves a card on (US-019, Design reference § Primary
 * actions): shown on the card on hover and focus, and at the top of the
 * drawer. Pure: what an action needs from the page — the toasts, a re-read,
 * the drawer, a dialog — comes in as an {@link ActionHost}, so the card, the
 * drawer, a drop (US-025) and the `a` key (US-027) all run the same thing.
 */

/** The dialogs an action can open; the board page draws them once (`board/dialogs.tsx`). */
export type ActionDialog =
  | 'approve-idea'
  | 'mark-ready'
  | 'schedule'
  | 'mark-posted'
  | 'resume-hold'
  | 'request-changes'
  | 'unschedule'
  | 'undo-posting';

/** What an action may ask of the page it runs on. */
export interface ActionHost {
  readonly toast: Toaster;
  /** Reads the board and `useAppData()` again. */
  refresh(): Promise<void>;
  navigate(href: string): void;
  /**
   * Opens the card's drawer; `planning` asks for the planning conversation
   * (US-023 scrolls to it and focuses the reply box).
   */
  openDrawer(cardId: string, focus?: 'planning'): void;
  openDialog(dialog: ActionDialog, card: BoardCard): void;
}

export interface PrimaryAction {
  readonly label: string;
  readonly icon: IconName;
  /**
   * Does it. A refusal is thrown (an `ApiError` carries the server's
   * `message`); the caller shows it with `toast.error`.
   */
  readonly run: (host: ActionHost) => Promise<void>;
}

/** The card's primary action, or `null` when nothing moves it on from here. */
export function primaryAction(card: BoardCard): PrimaryAction | null {
  return card.type === 'idea' ? ideaAction(card) : contentAction(card);
}

function ideaAction(card: IdeaCard): PrimaryAction | null {
  if (card.idea.status === 'drafted') {
    return dialog(wording.ACTION_APPROVE_IDEA, 'check', 'approve-idea', card);
  }
  if (card.idea.status === 'failed') {
    return {
      label: wording.ACTION_TRY_AGAIN,
      icon: 'sync',
      run: (host) =>
        settle(host, async () => {
          await retryIdeaBatch(card.batch.id);
          return { text: wording.BATCH_RETRIED };
        }),
    };
  }
  return null;
}

function contentAction(card: ContentCard): PrimaryAction | null {
  const content = card.content;
  switch (content.status) {
    case 'pending':
      return pendingAction(card);
    case 'ready':
      if (content.queuePosition !== null) return null;
      return {
        label: wording.ACTION_START_PRODUCTION,
        icon: 'play',
        run: (host) => settle(host, () => startProductionOf(content.id, content.name)),
      };
    case 'waiting':
      return dialog(wording.ACTION_RESUME_NOW, 'play', 'resume-hold', card);
    case 'approval':
      return {
        label: wording.ACTION_OPEN_TO_APPROVE,
        icon: 'eye',
        run: (host) => {
          host.navigate(contentPath(content.id));
          return Promise.resolve();
        },
      };
    case 'failed':
      if (content.failureStage === 'budget') {
        return {
          label: wording.ACTION_RAISE_CAP,
          icon: 'link-external',
          run: (host) => {
            host.navigate(contentPath(content.id));
            return Promise.resolve();
          },
        };
      }
      return {
        label: wording.ACTION_RETRY,
        icon: 'sync',
        run: (host) =>
          settle(host, async () => {
            const outcome = await retryContent(content.id);
            if (!outcome.ok) throw new Error(outcome.message ?? wording.ACTION_RETRY_REFUSED);
            return {
              text:
                outcome.plan.action === 'setup'
                  ? wording.actionSetUpAgain(content.name)
                  : wording.actionResumes(content.name, outcome.plan.resumeStep?.stepId ?? null),
            };
          }),
      };
    case 'review':
      if (content.autoReview !== null) return null;
      return {
        label: wording.actionApproveVersion(content.currentVersion),
        icon: 'check',
        run: (host) => settle(host, () => approveVersionOf(content.id, content.name)),
      };
    case 'delivered':
      if (card.column === 'to-post') return dialog(wording.ACTION_SCHEDULE, 'calendar', 'schedule', card);
      if (card.column === 'scheduled') return dialog(wording.ACTION_MARK_POSTED, 'check-circle', 'mark-posted', card);
      return null;
    // Nothing moves a rejected content on; un-rejecting is on its page.
    case 'rejected':
      return null;
    default:
      return null;
  }
}

/** Planning: the first of setup failed, a finished plan, a question, or nobody planning yet. */
function pendingAction(card: ContentCard): PrimaryAction | null {
  const content = card.content;
  if (content.lastError !== null) {
    return {
      label: wording.ACTION_FETCH_AGAIN,
      icon: 'sync',
      run: (host) =>
        settle(host, async () => {
          const result = await retryContentSetup(content.id);
          if (result.setup !== null && !result.setup.ok) throw new Error(result.setup.message);
          return { text: wording.actionSetUp(content.name) };
        }),
    };
  }
  if (content.planParses) return dialog(wording.ACTION_MARK_READY, 'check', 'mark-ready', card);
  if (content.planningState === 'your-turn' || content.planningState === 'failed') {
    return {
      label: wording.ACTION_ANSWER_CLAUDE,
      icon: 'comment',
      run: (host) => {
        host.openDrawer(card.cardId, 'planning');
        return Promise.resolve();
      },
    };
  }
  // Autopilot (US-021): the planner decided instead of asking; show what it chose.
  if (content.planningState === 'decided') {
    return {
      label: wording.ACTION_SEE_DECISIONS,
      icon: 'comment',
      run: (host) => {
        host.openDrawer(card.cardId, 'planning');
        return Promise.resolve();
      },
    };
  }
  // Like "Start planning" in § Needs you: only once setup has made the container.
  if (content.planningState === 'none' && content.containerId !== null) {
    return {
      label: wording.ACTION_PLAN_WITH_CLAUDE,
      icon: 'comment',
      run: async (host) => {
        await startHeadlessPlanning(content.id);
        host.toast.ok(wording.actionPlanningStarted(content.name));
        host.openDrawer(card.cardId, 'planning');
        await host.refresh();
      },
    };
  }
  return null;
}

/** **Approve version {n}**, shared with a drop from Review on To post (US-025). */
export async function approveVersionOf(id: string, name: string): Promise<Settled> {
  const result = await approveContent(id);
  return { text: wording.actionVersionApproved(name, result.version) };
}

/**
 * **Start production**, shared with MarkReadyDialog's "straight away". A start
 * beyond the slots is a queue place, not a refusal; so is a start during
 * Claude's usage-limit hold, which the server answers with 429
 * `usage_limit_hold` after queueing the content.
 */
export async function startProductionOf(id: string, name: string): Promise<Settled> {
  try {
    const view = await startProduction(id);
    return {
      text: view.queued
        ? wording.actionQueued(name, view.queuePosition ?? 1)
        : wording.actionProducing(name),
    };
  } catch (cause: unknown) {
    if (cause instanceof ApiError && cause.code === 'usage_limit_hold') {
      return { text: wording.ACTION_QUEUED_FOR_HOLD, kind: 'info' };
    }
    throw cause;
  }
}

/** What a finished action tells the operator; `href` makes the toast a link. */
export interface Settled {
  readonly text: string;
  readonly href?: string;
  readonly kind?: 'ok' | 'info';
}

/** Runs the call, shows how it went, and reads the board and the app data again. */
export async function settle(host: ActionHost, work: () => Promise<Settled>): Promise<void> {
  const outcome = await work();
  if (outcome.kind === 'info') host.toast.info(outcome.text);
  else host.toast.ok(outcome.text, outcome.href === undefined ? undefined : { href: outcome.href });
  await host.refresh();
}

function dialog(label: string, icon: IconName, kind: ActionDialog, card: BoardCard): PrimaryAction {
  return {
    label,
    icon,
    run: (host) => {
      host.openDialog(kind, card);
      return Promise.resolve();
    },
  };
}
